import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ParticleCanvas } from "../components/ParticleCanvas";
import { ParallaxBg } from "../components/ParallaxBg";
import { ReplayPlayer } from "../components/ReplayPlayer";
import Ticker from "../sections/Ticker";
import { R_TICKER, R_HOST, REPLAY_WINDOW_HOURS } from "../lib/replayCopy";
import { RManifesto, RMethod, ROfferStack, RGuarantee, scrollToBook } from "./replay/ReplaySections";
import { RHost, RObjections, RFaq, RFinalBooking, RFooter } from "./replay/ReplaySections2";

gsap.registerPlugin(ScrollTrigger);

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

function ReplayNav() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(t);
  }, []);
  return (
    <nav className={`nav-pill ${visible ? "visible" : ""}`} data-testid="replay-nav">
      <button className="nav-logo-btn" onClick={() => scrollToId("watch")} data-testid="replay-nav-logo">
        <img src="/nexa-logo.png" alt="NEXA Lending" />
      </button>
      <button className="nav-link" data-testid="replay-nav-watch" onClick={() => scrollToId("watch")}>WATCH</button>
      <button className="nav-link" data-testid="replay-nav-method" onClick={() => scrollToId("method")}>THE METHOD</button>
      <button className="nav-link" data-testid="replay-nav-host" onClick={() => scrollToId("host")}>HOST</button>
      <button className="nav-link" data-testid="replay-nav-faq" onClick={() => scrollToId("faq")}>FAQ</button>
      <button className="nav-cta" data-testid="replay-nav-book-cta" onClick={scrollToBook}>BOOK MY CALL ↗</button>
    </nav>
  );
}

function ExpiryCountdown() {
  const [left, setLeft] = useState("");
  useEffect(() => {
    let start = Number(localStorage.getItem("replay_first_visit"));
    if (!start) {
      start = Date.now();
      localStorage.setItem("replay_first_visit", String(start));
    }
    const end = start + REPLAY_WINDOW_HOURS * 3600000;
    const tick = () => {
      const d = Math.max(0, end - Date.now());
      const h = Math.floor(d / 3600000);
      const m = String(Math.floor(d / 60000) % 60).padStart(2, "0");
      const s = String(Math.floor(d / 1000) % 60).padStart(2, "0");
      setLeft(`${h}H ${m}M ${s}S`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <p className="mono" style={{ fontSize: 11, letterSpacing: "0.16em", color: "#3D7BFF" }} data-testid="replay-expiry-line">
      <i className="fa-regular fa-hourglass-half mr-2"></i>
      THIS REPLAY IS AVAILABLE FOR {REPLAY_WINDOW_HOURS} HOURS FROM YOUR REGISTRATION — {left} LEFT
    </p>
  );
}

function ReplayHero({ watchPctRef }) {
  const heroRef = useRef(null);
  useEffect(() => {
    const lines = heroRef.current.querySelectorAll(".h1-inner");
    gsap.to(lines, { y: 0, duration: 1.1, ease: "power3.out", stagger: 0.12, delay: 0.3 });
    gsap.fromTo(
      heroRef.current.querySelectorAll(".hero-fade"),
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.9, ease: "power2.out", stagger: 0.1, delay: 0.85 }
    );
  }, []);

  return (
    <section id="watch" ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="replay-hero-section">
      <ParallaxBg image="/bg/home3.jpg" />
      <div className="absolute inset-0 hidden md:block" style={{ opacity: 0.55 }}>
        <ParticleCanvas scrollTriggerRef={heroRef} finalShape="arch" />
      </div>
      <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-12 pt-28 pb-16 grid lg:grid-cols-[45fr_55fr] gap-12 items-center relative">
        <div>
          <p className="eyebrow mb-6 hero-fade" data-testid="replay-eyebrow">YOUR REPLAY IS READY — WATCH BEFORE IT EXPIRES</p>
          <h1 className="hero-h1 mb-7" style={{ fontSize: "clamp(36px, 4.8vw, 68px)" }} data-testid="replay-h1">
            <span className="h1-line"><span className="h1-inner line-accent">THE VA HOME-READY BLUEPRINT —</span></span>
            <span className="h1-line"><span className="h1-inner">WATCH THE FULL REPLAY</span></span>
            <span className="h1-line"><span className="h1-inner line-dim">FROM SOMEONE WHO UNDERSTANDS THE WHOLE PROCESS</span></span>
          </h1>
          <p className="hero-fade mb-6" style={{ fontSize: 15, color: "rgba(240,236,228,0.55)", lineHeight: 1.7, maxWidth: 500 }}>
            Watch the full training below, then book your free VA Buyer Pre-Approval Strategy Call — your eligibility and entitlement explained in plain English by a licensed Mortgage Loan Advisor, same-week answers, no hard credit pull.
          </p>
          <blockquote className="hero-blockquote hero-fade mb-7" style={{ maxWidth: 460 }}>
            What if your Mortgage Loan Advisor could explain your numbers, anticipate financing issues, and work hand-in-hand with your Realtor toward one goal — your closing?
          </blockquote>
          <div className="hero-fade mb-10"><ExpiryCountdown /></div>
          <div className="hero-fade flex items-center gap-4" data-testid="replay-host-module">
            <img src="/matthew.jpg" alt="Matthew Lake-Nestor" className="avatar-photo" />
            <div>
              <p className="font-bold text-sm">{R_HOST.name}</p>
              <p className="mono" style={{ fontSize: 10, letterSpacing: "0.12em", color: "var(--muted)" }}>
                NEXA LENDING · NMLS# {R_HOST.nmls}
              </p>
            </div>
          </div>
        </div>
        <div className="relative">
          <ReplayPlayer onMilestone={(m) => { watchPctRef.current = m; }} />
          <button className="btn-accent w-full mt-5 hero-fade" data-testid="replay-hero-cta" onClick={scrollToBook}>
            ✓ BOOK MY FREE STRATEGY CALL
          </button>
          <p className="text-center mt-3 hero-fade" style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", letterSpacing: "0.08em" }}>
            Free · No hard credit pull · Written plan included
          </p>
        </div>
      </div>
    </section>
  );
}

export default function Replay() {
  const watchPctRef = useRef(0);

  useEffect(() => {
    document.title = "Watch the Replay — The VA Home-Ready Blueprint | Matthew Lake-Nestor";
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    window.__lenis = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    const raf = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);
    return () => {
      gsap.ticker.remove(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="wam-page" data-testid="replay-page">
      <div className="grain-overlay" />
      <ReplayNav />
      <main>
        <ReplayHero watchPctRef={watchPctRef} />
        <Ticker items={R_TICKER} />
        <RManifesto />
        <RMethod />
        <ROfferStack />
        <RGuarantee />
        <RHost />
        <RObjections />
        <RFaq />
        <RFinalBooking />
        <RFooter />
      </main>
      <div className="mobile-cta" data-testid="replay-mobile-cta">
        <button className="btn-accent w-full" onClick={scrollToBook} data-testid="replay-mobile-cta-button">
          ✓ BOOK MY FREE STRATEGY CALL
        </button>
      </div>
    </div>
  );
}
